/**
 * Admin → Resources → White Papers.
 *
 * Route: /admin/whitepapers
 *
 * Sharable resources for 1-on-1 sales conversations. Each entry
 * exposes: preview link (public URL), copy-link button (for sending
 * in email/DM), and raw-markdown copy (for pasting into docs or
 * shared drives).
 */
import { useState } from "react";
import { toast } from "sonner";
import { ToolPageShell } from "@/components/AdminTools";
import { Copy, Download, ExternalLink, FileText, Link as LinkIcon } from "lucide-react";
import { WHITEPAPERS } from "@/data/whitepapers";

export default function AdminWhitepapers() {
  const [copyBusy, setCopyBusy] = useState(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const copyLink = async (paper) => {
    setCopyBusy(paper.id + ":link");
    try {
      await navigator.clipboard.writeText(`${origin}${paper.public_path}`);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed — select the link manually.");
    } finally {
      setCopyBusy(null);
    }
  };

  const copyMarkdown = async (paper) => {
    setCopyBusy(paper.id + ":md");
    try {
      const r = await fetch(paper.markdown_path);
      if (!r.ok) throw new Error("fetch failed");
      const text = await r.text();
      await navigator.clipboard.writeText(text);
      toast.success("Markdown copied to clipboard");
    } catch {
      toast.error("Copy failed. Try the download link instead.");
    } finally {
      setCopyBusy(null);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Resources"
      title="Sales White Papers"
      intro="Long-form thought-leadership pieces published on the public site. Use in prospect follow-ups, LinkedIn shares, and deal-critical objection-handling moments."
    >
      <div className="grid grid-cols-1 gap-4" data-testid="admin-whitepapers-list">
        {WHITEPAPERS.map((paper) => (
          <div
            key={paper.id}
            data-testid={`whitepaper-card-${paper.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-6"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-600">
                  <FileText className="w-3 h-3" /> {paper.tag}
                </div>
                <h3 className="mt-2 font-display text-lg sm:text-xl font-bold text-slate-900 leading-tight">
                  {paper.title}
                </h3>
                <div className="mt-1 text-[11px] text-slate-500">
                  {paper.read_minutes} min read · Published {paper.published}
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{paper.summary}</p>

                {paper.og_image_path && (
                  <a
                    href={paper.og_image_path}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 block rounded-lg overflow-hidden border border-slate-200 hover:border-blue-300 transition-colors"
                    data-testid={`whitepaper-og-preview-${paper.id}`}
                    title="Social share preview — LinkedIn, Twitter, Slack"
                  >
                    <img
                      src={paper.og_image_path}
                      alt={`Social share preview for ${paper.title}`}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </a>
                )}

                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                    Use when
                  </div>
                  <ul className="text-xs text-slate-700 space-y-1 list-disc pl-4">
                    {paper.use_when.map((u, i) => (
                      <li key={i}>{u}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex flex-col gap-2 min-w-[200px]">
                {paper.pdf_path && (
                  <a
                    href={paper.pdf_path}
                    download
                    data-testid={`whitepaper-download-pdf-${paper.id}`}
                    className="inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-2 text-sm font-semibold"
                    title={`${paper.pdf_pages || "—"} pages · ${paper.pdf_size_kb || "?"} KB`}
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </a>
                )}
                <a
                  href={paper.public_path}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`whitepaper-open-${paper.id}`}
                  className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-sm font-semibold"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open public page
                </a>
                <button
                  type="button"
                  onClick={() => copyLink(paper)}
                  disabled={copyBusy === paper.id + ":link"}
                  data-testid={`whitepaper-copy-link-${paper.id}`}
                  className="inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
                >
                  <LinkIcon className="w-3.5 h-3.5" /> Copy link
                </button>
                <button
                  type="button"
                  onClick={() => copyMarkdown(paper)}
                  disabled={copyBusy === paper.id + ":md"}
                  data-testid={`whitepaper-copy-md-${paper.id}`}
                  className="inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy markdown
                </button>
                <a
                  href={paper.markdown_path}
                  download
                  data-testid={`whitepaper-download-${paper.id}`}
                  className="inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold"
                >
                  Download .md
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Marketing flyers — one-page recruitment/sales assets */}
      <div className="mt-10" data-testid="admin-flyers-section">
        <div className="mb-4">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-1">
            Admin · Marketing
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Marketing Flyers</h2>
          <p className="mt-1.5 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Print-ready one-page PDFs for LinkedIn posts, email attachments, booth handouts,
            and DM follow-ups.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4" data-testid="admin-flyers-list">
          <div
            data-testid="flyer-card-vendor-network"
            className="rounded-2xl border border-slate-200 bg-white p-6"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-blue-700">
                    Vendor recruitment · Beta launch
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">
                  Nodera Health Vendor Network — Launch Flyer
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
                  One-page US Letter PDF announcing the opening of the vendor network. Includes
                  the 3-stage runbar (Activate → Launch → Dispatch), recruited categories, CTA,
                  and QR code linking to <span className="font-mono text-slate-800">/vendors/apply</span>.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href="/flyers/vendor-network.pdf"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="flyer-preview-vendor-network"
                  className="inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Preview
                </a>
                <a
                  href="/flyers/vendor-network.pdf"
                  download
                  data-testid="flyer-download-vendor-network"
                  className="inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-full px-4 py-2 text-sm font-semibold"
                >
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </a>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Format</div>
                <div className="text-slate-800 font-semibold mt-0.5">Letter · 1 page</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Best for</div>
                <div className="text-slate-800 font-semibold mt-0.5">LinkedIn · Email · Print</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">CTA</div>
                <div className="text-slate-800 font-semibold mt-0.5">/vendors/apply</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Regen</div>
                <div className="text-slate-800 font-semibold mt-0.5 font-mono">render_vendor_flyer.py</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ToolPageShell>
  );
}
